// Перечитывание подписки после оплаты.
//
// Бэк активирует подписку АСИНХРОННО, уже после того как платёж стал
// `success`: в момент возврата с кассы `GET /subscriptions/me` ещё может
// отвечать «подписки нет». Поэтому спрашиваем несколько раз с растущей паузой,
// пока подписка не появится. Правила — чистой функцией (покрыта тестом),
// таймеры — в `syncSubscriptionAfterPayment`.
import { useUserStore } from "@/entities/user/model/store";
import { resolveAccountId } from "./selectors";
import { useSubscriptionStore } from "./store";
/** Когда спрашивать подписку, мс от момента оплаты. */
export const ACTIVATION_POLL_AT_MS = [0, 2000, 5000, 10000];
/**
 * Следующий шаг серии: сделать ещё запрос через `delayMs` или остановиться.
 * Останавливаемся, когда сменился пользователь, подписка появилась, попытки
 * кончились либо подписку и не ждали.
 */
export function planActivationPoll({ attempt, hasSubscription, sameUser, expectSubscription, }) {
    if (!sameUser)
        return { kind: "stop" };
    if (attempt === 0)
        return { kind: "load", delayMs: ACTIVATION_POLL_AT_MS[0] };
    if (hasSubscription || !expectSubscription)
        return { kind: "stop" };
    if (attempt >= ACTIVATION_POLL_AT_MS.length)
        return { kind: "stop" };
    return {
        kind: "load",
        delayMs: ACTIVATION_POLL_AT_MS[attempt] - ACTIVATION_POLL_AT_MS[attempt - 1],
    };
}
// ── «Ждём активации»: оформление подписки начато в этой вкладке ─────────────
/** Сколько ждём активации после ухода на кассу. */
export const ACTIVATION_WAIT_MS = 30 * 60 * 1000;
let awaitingUntil = 0;
/** Пользователь ушёл на кассу оформлять подписку. */
export function markSubscriptionCheckoutStarted(now = Date.now()) {
    awaitingUntil = now + ACTIVATION_WAIT_MS;
}
/** Ждём ли ещё появления подписки после начатого оформления. */
export function isAwaitingSubscriptionActivation(now = Date.now()) {
    return now < awaitingUntil;
}
function clearAwaiting() {
    awaitingUntil = 0;
}
/**
 * WS-уведомления, после которых подписка или баланс могли измениться. Типы —
 * те же, что показывает центр уведомлений (`payment_*`, `subscription_*`).
 */
export function isSubscriptionRelatedNotification(type) {
    const t = (type ?? "").toLowerCase();
    return t === "payment_success" || t.startsWith("subscription_");
}
// ── Исполнение ──────────────────────────────────────────────────────────────
let onActivated = null;
let runToken = 0;
/**
 * Что сделать, когда подписка появилась (обновить баланс: песни по подписке
 * начисляются вместе с активацией). Регистрирует `useSubscriptionSync`.
 */
export function registerSubscriptionActivatedHandler(fn) {
    onActivated = fn;
}
/**
 * Перечитать подписку после оплаты: сразу и, если ждём именно подписку, ещё
 * несколько раз (см. `ACTIVATION_POLL_AT_MS`), пока она не появится. Новая
 * серия отменяет предыдущую. Гостю и неавторизованному — ничего не делает.
 */
export function syncSubscriptionAfterPayment(options = {}) {
    const userId = resolveAccountId(useUserStore.getState());
    if (userId === null)
        return;
    const expectSubscription = options.expectSubscription ?? isAwaitingSubscriptionActivation();
    const hadSubscription = (() => {
        const s = useSubscriptionStore.getState();
        return s.userId === userId && s.subscription !== null;
    })();
    const token = ++runToken;
    let attempt = 0;
    const step = () => {
        if (token !== runToken)
            return;
        const store = useSubscriptionStore.getState();
        const plan = planActivationPoll({
            attempt,
            hasSubscription: store.userId === userId && store.subscription !== null,
            sameUser: resolveAccountId(useUserStore.getState()) === userId,
            expectSubscription,
        });
        if (plan.kind === "stop") {
            const active = useSubscriptionStore.getState();
            if (active.userId === userId && active.subscription !== null) {
                clearAwaiting();
                // Подписка появилась в этой серии — вместе с ней начислены песни.
                if (!hadSubscription)
                    onActivated?.();
            }
            return;
        }
        setTimeout(() => {
            if (token !== runToken)
                return;
            attempt += 1;
            void useSubscriptionStore
                .getState()
                .load(userId, { force: true })
                .then(step);
        }, plan.delayMs);
    };
    step();
}
